import { ArrowLeftIcon, LoaderCircleIcon } from 'lucide-react'

import { Button, Checkbox } from 'slab'
import {
  type ResourceAccessTokensResourceAccessTokenAbilitiesItem,
  type ResourceAccessTokensResourceAccessTokenRecipient as ResourceAccessTokenRecipient,
} from 'utils/web_api.schemas'

type EditPermissionsViewProps = {
  recipient: ResourceAccessTokenRecipient,
  editingAbilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[],
  originalAbilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[],
  canSendEditorInvites: boolean,
  canSendOnboardInvites: boolean,
  isSaving: boolean,
  onToggleAbility: (ability: ResourceAccessTokensResourceAccessTokenAbilitiesItem) => void,
  onSave: () => void,
  onCancel: () => void
}

export const EditPermissionsView = ({
  recipient,
  editingAbilities,
  originalAbilities,
  canSendEditorInvites,
  canSendOnboardInvites,
  isSaving,
  onToggleAbility,
  onSave,
  onCancel,
}: EditPermissionsViewProps) => {
  return (
    <div className='space-y-4'>
      <button
        onClick={onCancel}
        className='flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors'
      >
        <ArrowLeftIcon className='w-4 h-4' />
        Back to recipients
      </button>
      <div className='text-sm text-gray-600'>
        Edit permissions for <span className='font-semibold'>{recipient.email}</span>
      </div>
      <div className='space-y-3 py-2'>
        <Checkbox
          label='Can view'
          description='Can view the quote (always enabled)'
          checked
          disabled
        />
        {/* Show edit checkbox: editable if user has edit permission, disabled if not but recipient has it */}
        {canSendEditorInvites
          ? (
            <Checkbox
              label='Can edit'
              description='Can make changes to the quote'
              checked={editingAbilities.includes('edit')}
              onChange={() => onToggleAbility('edit')}
            />
            )
          : originalAbilities.includes('edit') && (
            <Checkbox
              label='Can edit'
              description='You cannot modify this permission'
              checked
              disabled
            />
          )}
        {/* Show onboard checkbox: editable if user has onboard permission, disabled if not but recipient has it */}
        {canSendOnboardInvites
          ? (
            <Checkbox
              label='Can onboard'
              description='Can sign up their team with this allowance strategy'
              checked={editingAbilities.includes('onboard')}
              onChange={() => onToggleAbility('onboard')}
            />
            )
          : originalAbilities.includes('onboard') && (
            <Checkbox
              label='Can onboard'
              description='You cannot modify this permission'
              checked
              disabled
            />
          )}
      </div>
      <div className='flex justify-end gap-2 pt-2'>
        <Button variant='secondary' onClick={onCancel} size='small'>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={onSave}
          size='small'
          disabled={isSaving}
        >
          {isSaving
            ? (
              <div className='flex items-center gap-2'>
                Saving
                <LoaderCircleIcon className='w-4 h-4 animate-spin' />
              </div>
              )
            : 'Save permissions'}
        </Button>
      </div>
    </div>
  )
}
